import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';

const CATEGORIES = ['all', 'newsletter', 'partner', 'agency', 'community', 'podcast', 'youtube'];
const STATUSES = ['all', 'todo', 'contacted', 'replied', 'in_progress', 'won', 'lost'];
const WAVES = ['all', '1', '2', '3', 'none'];

const STATUS_COLORS = {
  todo: { bg: '#6b7280', text: '#fff' },
  contacted: { bg: '#3b82f6', text: '#fff' },
  replied: { bg: '#f59e0b', text: '#000' },
  in_progress: { bg: '#f97316', text: '#fff' },
  won: { bg: '#22c55e', text: '#fff' },
  lost: { bg: '#ef4444', text: '#fff' },
};

const CAT_COLORS = {
  newsletter: '#8b5cf6',
  partner: '#3b82f6',
  agency: '#06b6d4',
  community: '#10b981',
  podcast: '#f59e0b',
  youtube: '#ef4444',
};

const WAVE_COLORS = {
  1: { bg: '#22c55e', label: 'W1' },
  2: { bg: '#3b82f6', label: 'W2' },
  3: { bg: '#9ca3af', label: 'W3' },
};

function AdminCrm() {
  const { darkMode } = useTheme();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterWave, setFilterWave] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [activities, setActivities] = useState({});
  const [newNote, setNewNote] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState({ name: '', company: '', category: 'newsletter', status: 'todo', email: '', linkedin: '', website: '', notes: '', next_followup_at: '', wave: '', greeting_name: '' });
  const [sortBy, setSortBy] = useState('date');
  const [filterHasEmail, setFilterHasEmail] = useState(false);
  const [statusDropdownId, setStatusDropdownId] = useState(null);
  const statusDropdownRef = useRef(null);

  // Email preview state
  const [emailPreview, setEmailPreview] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const fetchContacts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory !== 'all') params.set('category', filterCategory);
      if (filterStatus !== 'all') params.set('status', filterStatus);
      if (filterWave !== 'all') params.set('wave', filterWave);
      if (sortBy !== 'date') params.set('sort', sortBy);
      const res = await axios.get(`/api/crm?${params}`);
      setContacts(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load contacts');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterStatus, filterWave, sortBy]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target)) {
        setStatusDropdownId(null);
      }
    };
    if (statusDropdownId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownId]);

  const fetchActivities = async (contactId) => {
    try {
      const res = await axios.get(`/api/crm/${contactId}/activities`);
      setActivities(prev => ({ ...prev, [contactId]: res.data }));
    } catch (err) {
      console.error('Failed to load activities:', err);
    }
  };

  const toggleExpand = (id) => {
    if (expandedId === id) {
      setExpandedId(null);
    } else {
      setExpandedId(id);
      if (!activities[id]) fetchActivities(id);
    }
  };

  const openAddModal = () => {
    setEditingContact(null);
    setForm({ name: '', company: '', category: 'newsletter', status: 'todo', email: '', linkedin: '', website: '', notes: '', next_followup_at: '', wave: '', greeting_name: '' });
    setShowModal(true);
  };

  const openEditModal = (contact) => {
    setEditingContact(contact);
    setForm({
      name: contact.name || '',
      company: contact.company || '',
      category: contact.category || 'newsletter',
      status: contact.status || 'todo',
      email: contact.email || '',
      linkedin: contact.linkedin || '',
      website: contact.website || '',
      notes: contact.notes || '',
      next_followup_at: contact.next_followup_at ? contact.next_followup_at.split('T')[0] : '',
      wave: contact.wave != null ? String(contact.wave) : '',
      greeting_name: contact.greeting_name || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, wave: form.wave ? parseInt(form.wave) : null, greeting_name: form.greeting_name || null };
      if (editingContact) {
        await axios.put(`/api/crm/${editingContact.id}`, payload);
      } else {
        await axios.post('/api/crm', payload);
      }
      setShowModal(false);
      fetchContacts();
      if (editingContact && expandedId === editingContact.id) {
        fetchActivities(editingContact.id);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contact?')) return;
    try {
      await axios.delete(`/api/crm/${id}`);
      if (expandedId === id) setExpandedId(null);
      fetchContacts();
    } catch (err) {
      alert('Delete failed');
    }
  };

  const handleAddNote = async (contactId) => {
    if (!newNote.trim()) return;
    try {
      await axios.post(`/api/crm/${contactId}/activities`, { type: 'note', description: newNote.trim() });
      setNewNote('');
      fetchActivities(contactId);
    } catch (err) {
      alert('Failed to add note');
    }
  };

  const handleQuickStatus = async (contact, newStatus) => {
    try {
      await axios.put(`/api/crm/${contact.id}`, { ...contact, status: newStatus, last_contacted_at: ['contacted', 'replied'].includes(newStatus) ? new Date().toISOString() : contact.last_contacted_at });
      fetchContacts();
      if (expandedId === contact.id) fetchActivities(contact.id);
    } catch (err) {
      alert('Status update failed');
    }
  };

  // Email preview
  const openEmailPreview = async (contact) => {
    try {
      const res = await axios.get(`/api/crm/${contact.id}/email-preview`);
      setEmailPreview({ contactId: contact.id, ...res.data });
      setEmailSent(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to load email preview');
    }
  };

  const handleSendEmail = async () => {
    if (!emailPreview || sendingEmail) return;
    if (!window.confirm(`Send outreach email to ${emailPreview.to}?`)) return;
    setSendingEmail(true);
    try {
      await axios.post(`/api/crm/${emailPreview.contactId}/send-email`, {
        subject: emailPreview.subject,
        greeting_name: emailPreview.greetingName,
      });
      setEmailSent('Email sent successfully!');
      fetchContacts();
      if (expandedId === emailPreview.contactId) fetchActivities(emailPreview.contactId);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSendTestEmail = async () => {
    if (!emailPreview || sendingEmail) return;
    setSendingEmail(true);
    try {
      const res = await axios.post(`/api/crm/${emailPreview.contactId}/send-test-email`, {
        subject: emailPreview.subject,
        greeting_name: emailPreview.greetingName,
      });
      setEmailSent(res.data.message || 'Test email sent!');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send test email');
    } finally {
      setSendingEmail(false);
    }
  };

  const updateEmailPreviewGreeting = (newName) => {
    if (!emailPreview) return;
    const updatedHtml = emailPreview.html.replace(
      /(Hi [^,<]+,|Hello,)/,
      newName ? `Hi ${newName},` : 'Hello,'
    );
    setEmailPreview(prev => ({ ...prev, greetingName: newName, html: updatedHtml }));
  };

  const formatDate = (d) => {
    if (!d) return '\u2014';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const bg = darkMode ? '#111827' : '#f9fafb';
  const cardBg = darkMode ? '#1f2937' : '#fff';
  const border = darkMode ? '#374151' : '#e5e7eb';
  const text = darkMode ? '#f3f4f6' : '#111827';
  const textMuted = darkMode ? '#9ca3af' : '#6b7280';
  const accent = '#7c4dff';

  // Client-side search filter
  const filtered = contacts.filter(c => {
    if (filterHasEmail && !c.email) return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (c.name || '').toLowerCase().includes(term) ||
           (c.company || '').toLowerCase().includes(term) ||
           (c.email || '').toLowerCase().includes(term) ||
           (c.notes || '').toLowerCase().includes(term);
  });

  if (loading) return <div style={{ padding: 20, color: text }}>Loading CRM...</div>;
  if (error) return <div style={{ padding: 20, color: '#ef4444' }}>{error}</div>;

  return (
    <div style={{ padding: '20px', background: bg, minHeight: '100vh' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0, color: text, fontSize: 24 }}>Affiliate CRM</h1>
        <button onClick={openAddModal} style={{ padding: '8px 16px', background: accent, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}>
          + Add Contact
        </button>
      </div>

      {/* Category filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setFilterCategory(c)}
            style={{
              padding: '5px 12px', borderRadius: 20, border: `1px solid ${border}`, cursor: 'pointer', fontSize: 13,
              background: filterCategory === c ? accent : cardBg,
              color: filterCategory === c ? '#fff' : text,
            }}>
            {c === 'all' ? 'All' : c.charAt(0).toUpperCase() + c.slice(1)}
          </button>
        ))}
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${border}`, background: cardBg, color: text, fontSize: 13 }}>
          {STATUSES.map(s => (
            <option key={s} value={s}>{s === 'all' ? 'All Statuses' : s.replace('_', ' ')}</option>
          ))}
        </select>
      </div>

      {/* Wave filter + Search */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
        {WAVES.map(w => (
          <button key={w} onClick={() => setFilterWave(w)}
            style={{
              padding: '5px 12px', borderRadius: 20, border: `1px solid ${border}`, cursor: 'pointer', fontSize: 13,
              background: filterWave === w ? (w === 'none' ? '#6b7280' : w === 'all' ? accent : (WAVE_COLORS[w] || {}).bg || accent) : cardBg,
              color: filterWave === w ? '#fff' : text,
            }}>
            {w === 'all' ? 'All Waves' : w === 'none' ? 'No Wave' : `Wave ${w}`}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={() => setFilterHasEmail(!filterHasEmail)}
          style={{
            padding: '5px 12px', borderRadius: 20, border: `1px solid ${border}`, cursor: 'pointer', fontSize: 13,
            background: filterHasEmail ? '#22c55e' : cardBg,
            color: filterHasEmail ? '#fff' : text,
          }}>
          Has Email
        </button>
        <input
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search contacts..."
          style={{ padding: '6px 12px', borderRadius: 6, border: `1px solid ${border}`, background: cardBg, color: text, fontSize: 13, minWidth: 180 }}
        />
      </div>

      {/* Stats bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, fontSize: 13, color: textMuted }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <span>{filtered.length} contact{filtered.length !== 1 ? 's' : ''}</span>
          <span>{filtered.filter(c => c.status === 'todo').length} todo</span>
          <span>{filtered.filter(c => c.status === 'contacted').length} contacted</span>
          <span>{filtered.filter(c => c.status === 'won').length} won</span>
        </div>
        <button
          onClick={() => {
            if (sortBy === 'date') setSortBy('name_asc');
            else if (sortBy === 'name_asc') setSortBy('name_desc');
            else if (sortBy === 'name_desc') setSortBy('wave');
            else setSortBy('date');
          }}
          style={{
            padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
            border: `1px solid ${border}`,
            background: sortBy !== 'date' ? accent : cardBg,
            color: sortBy !== 'date' ? '#fff' : text,
          }}
        >
          {sortBy === 'name_asc' ? 'Name A\u2192Z' : sortBy === 'name_desc' ? 'Name Z\u2192A' : sortBy === 'wave' ? 'Sort: Wave' : 'Sort: Date'}
        </button>
      </div>

      {/* Contact list */}
      {filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: textMuted }}>No contacts found</div>
      ) : (
        filtered.map(contact => (
          <div key={contact.id} style={{ background: cardBg, border: `1px solid ${border}`, borderRadius: 8, marginBottom: 8, overflow: 'visible' }}>
            {/* Row */}
            <div
              onClick={() => toggleExpand(contact.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 600, color: text, fontSize: 14 }}>{contact.name}</span>
                  {contact.company && contact.company !== contact.name && <span style={{ color: textMuted, fontSize: 13 }}>{'\u2014'} {contact.company}</span>}
                </div>
              </div>
              {/* Wave badge */}
              {contact.wave && WAVE_COLORS[contact.wave] && (
                <span style={{
                  padding: '2px 7px', borderRadius: 12, fontSize: 10, fontWeight: 700,
                  background: WAVE_COLORS[contact.wave].bg, color: '#fff',
                  letterSpacing: '0.5px',
                }}>
                  {WAVE_COLORS[contact.wave].label}
                </span>
              )}
              <span style={{
                padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500,
                background: CAT_COLORS[contact.category] || '#6b7280', color: '#fff',
              }}>
                {contact.category}
              </span>
              <div style={{ position: 'relative' }}
                ref={statusDropdownId === contact.id ? statusDropdownRef : null}
              >
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setStatusDropdownId(statusDropdownId === contact.id ? null : contact.id);
                  }}
                  style={{
                    padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 500, cursor: 'pointer',
                    background: (STATUS_COLORS[contact.status] || STATUS_COLORS.todo).bg,
                    color: (STATUS_COLORS[contact.status] || STATUS_COLORS.todo).text,
                  }}
                >
                  {contact.status.replace('_', ' ')} &#9662;
                </span>
                {statusDropdownId === contact.id && (
                  <div style={{
                    position: 'absolute', top: '100%', right: 0, marginTop: 4,
                    background: cardBg, border: `1px solid ${border}`, borderRadius: 8,
                    padding: '4px 0', zIndex: 100, minWidth: 130,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  }}>
                    {STATUSES.filter(s => s !== 'all' && s !== contact.status).map(s => (
                      <div
                        key={s}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickStatus(contact, s);
                          setStatusDropdownId(null);
                        }}
                        style={{
                          padding: '6px 12px', fontSize: 12, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', gap: 8, color: text,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = darkMode ? '#374151' : '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%', display: 'inline-block',
                          background: (STATUS_COLORS[s] || STATUS_COLORS.todo).bg,
                        }} />
                        {s.replace('_', ' ')}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 12, color: textMuted, minWidth: 80, textAlign: 'right' }}>
                {formatDate(contact.last_contacted_at)}
              </span>
              <span style={{ color: textMuted, fontSize: 12 }}>{expandedId === contact.id ? '\u25BE' : '\u25B8'}</span>
            </div>

            {/* Expanded detail */}
            {expandedId === contact.id && (
              <div style={{ padding: '0 16px 16px', borderTop: `1px solid ${border}` }}>
                <div style={{ display: 'flex', gap: 24, paddingTop: 12, flexWrap: 'wrap' }}>
                  {/* Left: details */}
                  <div style={{ flex: 1, minWidth: 250 }}>
                    <div style={{ fontSize: 13, color: textMuted, marginBottom: 8 }}>
                      {contact.email && <div>Email: <a href={`mailto:${contact.email}`} style={{ color: '#3b82f6' }}>{contact.email}</a></div>}
                      {contact.linkedin && <div>LinkedIn: <a href={contact.linkedin.startsWith('http') ? contact.linkedin : `https://${contact.linkedin}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>Profile</a></div>}
                      {contact.website && <div>Website: <a href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>{contact.website}</a></div>}
                      {contact.next_followup_at && <div>Follow-up: {formatDate(contact.next_followup_at)}</div>}
                      {contact.wave && <div>Wave: <strong>{contact.wave}</strong></div>}
                    </div>
                    {contact.notes && <div style={{ fontSize: 13, color: text, padding: '8px 0', whiteSpace: 'pre-wrap' }}>{contact.notes}</div>}

                    {/* Quick status */}
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                      {STATUSES.filter(s => s !== 'all' && s !== contact.status).map(s => (
                        <button key={s} onClick={(e) => { e.stopPropagation(); handleQuickStatus(contact, s); }}
                          style={{
                            padding: '3px 10px', borderRadius: 12, border: `1px solid ${border}`, cursor: 'pointer', fontSize: 11,
                            background: (STATUS_COLORS[s] || STATUS_COLORS.todo).bg,
                            color: (STATUS_COLORS[s] || STATUS_COLORS.todo).text,
                          }}>
                          {s.replace('_', ' ')}
                        </button>
                      ))}
                    </div>

                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button onClick={() => openEditModal(contact)} style={{ padding: '5px 12px', background: accent, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Edit</button>
                      <button onClick={() => {
                        if (!contact.email) { alert('Add an email address first (click Edit)'); return; }
                        openEmailPreview(contact);
                      }} style={{ padding: '5px 12px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>
                        Email
                      </button>
                      <button onClick={() => handleDelete(contact.id)} style={{ padding: '5px 12px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Delete</button>
                    </div>
                  </div>

                  {/* Right: activity log */}
                  <div style={{ flex: 1, minWidth: 250 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: text, marginBottom: 8 }}>Activity</div>
                    <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 8 }}>
                      {(activities[contact.id] || []).length === 0 ? (
                        <div style={{ fontSize: 12, color: textMuted }}>No activity yet</div>
                      ) : (
                        (activities[contact.id] || []).map(a => (
                          <div key={a.id} style={{ fontSize: 12, padding: '4px 0', borderBottom: `1px solid ${border}` }}>
                            <span style={{ color: textMuted }}>{formatDate(a.created_at)}</span>
                            <span style={{ margin: '0 6px', color: text, fontWeight: a.type === 'status_change' || a.type === 'email_sent' ? 600 : 400 }}>{a.description}</span>
                          </div>
                        ))
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        value={newNote}
                        onChange={e => setNewNote(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAddNote(contact.id)}
                        placeholder="Add a note..."
                        style={{ flex: 1, padding: '6px 10px', borderRadius: 4, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12 }}
                      />
                      <button onClick={() => handleAddNote(contact.id)} style={{ padding: '6px 12px', background: accent, color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>Add</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setShowModal(false)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: cardBg, borderRadius: 12, padding: 24, width: '100%', maxWidth: 500, maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ margin: '0 0 16px', color: text, fontSize: 18 }}>{editingContact ? 'Edit Contact' : 'Add Contact'}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Name *', key: 'name', type: 'text' },
                { label: 'Company', key: 'company', type: 'text' },
                { label: 'Email', key: 'email', type: 'email' },
                { label: 'Greeting Name (first name for emails)', key: 'greeting_name', type: 'text' },
                { label: 'LinkedIn URL', key: 'linkedin', type: 'text' },
                { label: 'Website', key: 'website', type: 'text' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: 12, color: textMuted, marginBottom: 2, display: 'block' }}>{f.label}</label>
                  <input type={f.type} value={form[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              ))}
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: textMuted, marginBottom: 2, display: 'block' }}>Category</label>
                  <select value={form.category} onChange={e => setForm(prev => ({ ...prev, category: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13 }}>
                    {CATEGORIES.filter(c => c !== 'all').map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: textMuted, marginBottom: 2, display: 'block' }}>Status</label>
                  <select value={form.status} onChange={e => setForm(prev => ({ ...prev, status: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13 }}>
                    {STATUSES.filter(s => s !== 'all').map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: textMuted, marginBottom: 2, display: 'block' }}>Wave</label>
                  <select value={form.wave} onChange={e => setForm(prev => ({ ...prev, wave: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13 }}>
                    <option value="">No wave</option>
                    <option value="1">Wave 1</option>
                    <option value="2">Wave 2</option>
                    <option value="3">Wave 3</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: textMuted, marginBottom: 2, display: 'block' }}>Next Follow-up</label>
                  <input type="date" value={form.next_followup_at} onChange={e => setForm(prev => ({ ...prev, next_followup_at: e.target.value }))}
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <label style={{ fontSize: 12, color: textMuted, marginBottom: 2, display: 'block' }}>Notes</label>
                <textarea value={form.notes} onChange={e => setForm(prev => ({ ...prev, notes: e.target.value }))} rows={3}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 4, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13, resize: 'vertical', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '8px 16px', background: 'transparent', color: textMuted, border: `1px solid ${border}`, borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>Cancel</button>
              <button onClick={handleSave} style={{ padding: '8px 16px', background: accent, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                {editingContact ? 'Save' : 'Add Contact'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Preview Modal */}
      {emailPreview && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => { setEmailPreview(null); setEmailSent(false); }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: cardBg, borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{ padding: '16px 24px', borderBottom: `1px solid ${border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, color: text, fontSize: 18 }}>Email Preview</h2>
              <button onClick={() => { setEmailPreview(null); setEmailSent(false); }}
                style={{ background: 'none', border: 'none', color: textMuted, fontSize: 20, cursor: 'pointer', padding: '4px 8px' }}>&times;</button>
            </div>
            {/* Fields */}
            <div style={{ padding: '12px 24px', borderBottom: `1px solid ${border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13 }}>
                <span style={{ color: textMuted, minWidth: 55 }}>To:</span>
                <span style={{ color: text }}>{emailPreview.to}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, fontSize: 13 }}>
                <span style={{ color: textMuted, minWidth: 55 }}>Subject:</span>
                <input
                  value={emailPreview.subject}
                  onChange={e => setEmailPreview(prev => ({ ...prev, subject: e.target.value }))}
                  style={{ flex: 1, padding: '5px 8px', borderRadius: 4, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13 }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <span style={{ color: textMuted, minWidth: 55 }}>Greeting:</span>
                <span style={{ color: textMuted }}>Hi</span>
                <input
                  value={emailPreview.greetingName}
                  onChange={e => updateEmailPreviewGreeting(e.target.value)}
                  style={{ width: 150, padding: '5px 8px', borderRadius: 4, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13 }}
                />
              </div>
            </div>
            {/* Preview iframe */}
            <div style={{ flex: 1, overflow: 'auto', background: '#e5e7eb', padding: 16 }}>
              <iframe
                srcDoc={emailPreview.html}
                title="Email Preview"
                style={{ width: '100%', height: 500, border: 'none', borderRadius: 6, background: '#fff' }}
              />
            </div>
            {/* Footer */}
            <div style={{ padding: '12px 24px', borderTop: `1px solid ${border}`, display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
              {emailSent && <span style={{ color: '#22c55e', fontSize: 13, fontWeight: 600, marginRight: 'auto' }}>{emailSent}</span>}
              <button onClick={() => { setEmailPreview(null); setEmailSent(false); }}
                style={{ padding: '8px 16px', background: 'transparent', color: textMuted, border: `1px solid ${border}`, borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>
                Close
              </button>
              {!emailSent && (
                <>
                <button onClick={handleSendTestEmail} disabled={sendingEmail}
                  style={{ padding: '8px 20px', background: sendingEmail ? '#6b7280' : '#2563eb', color: '#fff', border: 'none', borderRadius: 6, cursor: sendingEmail ? 'not-allowed' : 'pointer', fontSize: 13 }}>
                  {sendingEmail ? 'Sending...' : 'Send Test to Me'}
                </button>
                <button onClick={handleSendEmail} disabled={sendingEmail}
                  style={{ padding: '8px 20px', background: sendingEmail ? '#6b7280' : accent, color: '#fff', border: 'none', borderRadius: 6, cursor: sendingEmail ? 'not-allowed' : 'pointer', fontSize: 13 }}>
                  {sendingEmail ? 'Sending...' : 'Send Email'}
                </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminCrm;
